import I18n from 'I18n';
import { DATA_WELL, DONUT, PIE } from 'reporting-data/constants/chartTypes';
import { getDimensions } from 'reporting-data/report/configReportGetters';
import { getReportChartType } from 'reporting-data/report/reportGetters';
import { isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { RELATIONAL } from 'reporting-snowflake/relational/schema/report-records';
import { VisualTypes } from 'reporting-snowflake/relational/schema/visual-records';
import { getMeasuresWithoutExcludedPrefixes } from 'reporting-snowflake/relational/utils/column-utils';
import { getReportVisualType, createRelationalReportFromBaseReport } from 'reporting-snowflake/relational/utils/report-utils';
import { getReportBreakdownEncoding, getReportColorEncoding } from 'reporting-snowflake/relational/utils/report-visual-utils';
import { getIsReportValidForDimensionEditorDropdown } from '../dimension-editor-dropdown/utils';
import getMeasureSizeRanges, { MeasureSizeReasons } from '../../lib/report-utils/getMeasureSizeRanges';
export const isBreakdownEditorValidForRelationalReport = report => {
  const relationalReport = createRelationalReportFromBaseReport(report);
  const visualType = getReportVisualType(relationalReport);
  return !(visualType === VisualTypes.TEXT || visualType === VisualTypes.TABLE || visualType === VisualTypes.PIVOT_TABLE || visualType === VisualTypes.GAUGE);
};
export const getIsReportValidForBreakdownEditorDropdown = ({
  report
}) => {
  // Relational reports: we will hide the breakdown editor if the visual type is not supported (to match CRB)
  if (isRelationalReport(report)) {
    return isBreakdownEditorValidForRelationalReport(report);
  }

  // Other reports: always show the breakdown editor if the report is eligible for the dimension editor. We will disable instead of hide it if the report is not eligible for breakdown
  return getIsReportValidForDimensionEditorDropdown({
    report
  });
};
const getRelationalReportDisabledConditions = report => {
  const {
    maxDimensions,
    reason
  } = getMeasureSizeRanges(report);
  const visualType = getReportVisualType(report);
  const metrics = getMeasuresWithoutExcludedPrefixes(report.columns);
  if ([PIE, DONUT].includes(visualType || '') && metrics.size <= 1) {
    return {
      isBreakdownUnsupported: false,
      hasNoDimension: false,
      invalidChartType: false
    };
  }
  return {
    isBreakdownUnsupported: maxDimensions < 2,
    hasNoDimension: false,
    invalidChartType: false,
    reason,
    isRelationalWithNumMetricsReason: true // For relational reports, we show NumMetrics tooltip when breakdown is unsupported
  };
};
const getConfigReportDisabledConditions = report => {
  const {
    maxDimensions,
    reason
  } = getMeasureSizeRanges(report);
  const chartType = getReportChartType(report);
  return {
    isBreakdownUnsupported: maxDimensions < 2,
    hasNoDimension: !getDimensions(report).first(),
    invalidChartType: [DONUT, DATA_WELL, PIE].includes(chartType || ''),
    reason
  };
};
const getDisabledReasonText = (conditions, reportIsRelational) => {
  const {
    isBreakdownUnsupported,
    hasNoDimension,
    invalidChartType,
    reason,
    isRelationalWithNumMetricsReason
  } = conditions;
  if (isBreakdownUnsupported && (reason === MeasureSizeReasons.NumMetrics || isRelationalWithNumMetricsReason)) {
    return I18n.text('settingLabels.dimension.tooltip.disabled');
  }
  if (invalidChartType || isBreakdownUnsupported && reason === MeasureSizeReasons.ChartType) {
    return I18n.text('report-customization-components.customization-components.breakdown-editor-dropdown.config.disabled.invalidChartType');
  }
  if (hasNoDimension && !reportIsRelational) {
    return I18n.text('report-customization-components.customization-components.breakdown-editor-dropdown.config.disabled.noDimension');
  }
  return '';
};
export const getIsDropdownDisabled = report => {
  const reportIsRelational = report.type === RELATIONAL;
  const conditions = reportIsRelational ? getRelationalReportDisabledConditions(report) : getConfigReportDisabledConditions(report);
  const isDisabled = conditions.isBreakdownUnsupported || conditions.hasNoDimension || conditions.invalidChartType;
  if (!isDisabled) {
    return {
      isDisabled: false
    };
  }
  return {
    isDisabled: true,
    disabledReason: getDisabledReasonText(conditions, reportIsRelational)
  };
};
export const getIsClearButtonDisabled = report => getDimensions(report).count() < 2;

/**
 * Gets the current breakdown column from a relational report's color or breakdown encoding
 */
export const getCurrentBreakdownColumn = relationalReport => {
  const currentBreakdownEncoding = getReportColorEncoding(relationalReport) || getReportBreakdownEncoding(relationalReport);
  return currentBreakdownEncoding ? currentBreakdownEncoding.column : null;
};